import type { TPlayer } from 'video.js'
import type {
  AudioTrack,
  AudioTrackList,
  TextTrack,
  TextTrackList,
} from '../index.h'

import { isMobile } from '|>/shared/constants/device'
import {
  getSelectedAudioTrack,
  getSelectedTextTrack,
  isChromecastAPIAvailable,
} from '../lib'

/**
 * Stores the state of the current Chromecast session and its associated objects such
 * as the
 * [RemotePlayerController](https://developers.google.com/cast/docs/reference/chrome/cast.framework.RemotePlayerController),
 * and the
 * [RemotePlayer](https://developers.google.com/cast/docs/reference/chrome/cast.framework.RemotePlayer).
 *
 * WARNING: Do not instantiate this class until the
 * [CastContext](https://developers.google.com/cast/docs/reference/chrome/cast.framework.CastContext)
 * has been configured.
 *
 * For an undocumented (and thus unknown) reason, RemotePlayer and
 * RemotePlayerController instances created before the cast context has been configured
 * or after requesting a session or loading media will not stay in sync with media
 * items that are loaded later.
 *
 * For example, the first item that you cast will work as expected: events on
 * RemotePlayerController will fire and the state (currentTime, duration, etc) of the
 * RemotePlayer instance will update as the media item plays. However, if a new media
 * item is loaded via a `loadMedia` request, the media item will play, but the
 * remotePlayer will be in a "media unloaded" state where the duration is 0, the
 * currentTime does not update, and no change events are fired (except, strangely,
 * displayStatus updates).
 *
 */
export class ChromecastSessionManager {
  static isChromecastConnected: () => boolean
  static isChromecastSupported: () => boolean
  static hasConnected = false
  static prevTechState?: {
    sources?: any
    startTime?: number
    muted?: boolean
    paused?: boolean
    volume?: number
    audioTracks?: AudioTrackList
    textTracks?: TextTrackList
    initialAudio?: AudioTrack
    initialText?: TextTrack
    isMobile?: boolean
  }
  static remotePlayer: cast.framework.RemotePlayer | undefined = undefined
  static remotePlayerController:
    | cast.framework.RemotePlayerController
    | undefined = undefined
  static lastSessionId: string | undefined = undefined

  player: TPlayer
  _sessionListener: (event: any) => void
  _castListener: (event: any) => void

  constructor(player) {
    this.player = player

    // Create them in right moment as description says
    this.createRemotePlayer()

    // Add event listeners
    this._sessionListener = this._onSessionStateChange.bind(this)
    this._castListener =
      this._notifyPlayerOfDevicesAvailabilityChange.bind(this)
    this._addCastContextEventListeners()

    // Remove event listeners when this player instance is destroyed
    this.player.on('dispose', this._removeCastContextEventListeners.bind(this))

    // if session already exists
    this._notifyPlayerOfDevicesAvailabilityChange(
      this.getCastContext().getCastState()
    )

    this._onSessionSuccess = this._onSessionSuccess.bind(this)
  }

  /**
   * Add event listeners for events triggered on the current CastContext.
   */
  _addCastContextEventListeners() {
    this.getCastContext().addEventListener(
      cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      this._sessionListener
    )
    this.getCastContext().addEventListener(
      cast.framework.CastContextEventType.CAST_STATE_CHANGED,
      this._castListener
    )
  }

  /**
   * Remove event listeners that were added
   */
  _removeCastContextEventListeners() {
    this.getCastContext().removeEventListener(
      cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      this._sessionListener
    )
    this.getCastContext().removeEventListener(
      cast.framework.CastContextEventType.CAST_STATE_CHANGED,
      this._castListener
    )
  }

  /**
   * Handle session disconnect.
   */
  _onSessionDisconnect() {
    const startTime = this.player.currentTime()
    const currentVolume = this.player.volume()
    const currentMuted = this.player.muted()

    this.player.trigger('chromecastDisconnected', startTime)
    this.player.options_.chromecast?.onDisconnected?.(startTime)

    // TODO: what is it for, and how this work?
    let _prevTechState = ChromecastSessionManager.prevTechState ?? {}

    // save chromecast state to restore it on sender device
    _prevTechState = {
      sources: this.player.cachedSource,
      startTime,
      muted: currentMuted,
      paused: this.player.paused(),
      volume: _prevTechState.isMobile ? currentVolume : _prevTechState.volume,
    }

    ChromecastSessionManager.hasConnected = false

    this._reloadTech()
  }

  /**
   * Handle the CastContext's SessionState change event.
   */
  _onSessionStateChange(event) {
    const SessionState = cast.framework.SessionState

    if (
      event.sessionState === SessionState.SESSION_STARTED ||
      event.sessionState === SessionState.SESSION_RESUMED
    ) {
      // Session was created or resumed (including after a page reload).
      this._onSessionSuccess()
      return
    }

    if (event.sessionState === SessionState.SESSION_ENDED) {
      this._onSessionDisconnect()
    }
  }

  /**
   * Triggers player events that notifies listeners that Chromecast devices are
   * either available or unavailable.
   */
  _notifyPlayerOfDevicesAvailabilityChange(event) {
    if (this.hasAvailableDevices(event.castState)) {
      this.player.trigger('chromecastDevicesAvailable')
    } else {
      this.player.trigger('chromecastDevicesUnavailable')
    }
  }

  /**
   * Returns whether or not there are Chromecast devices available to cast to.
   */
  hasAvailableDevices(castState) {
    castState = castState || this.getCastContext().getCastState()

    return (
      castState === cast.framework.CastState.NOT_CONNECTED ||
      castState === cast.framework.CastState.CONNECTING ||
      castState === cast.framework.CastState.CONNECTED
    )
  }

  /**
   * Handles successful creation of a cast session
   */
  _onSessionSuccess() {
    const currentVolume = this.player.volume()
    const currentMuted = this.player.muted()
    // cast session created at this moment
    const castSession = this._getCurrentSession()

    // Do not allow to run initialization twice for the same Cast session.
    const sessionId = castSession?.getSessionId?.()
    if (
      sessionId &&
      ChromecastSessionManager.lastSessionId &&
      ChromecastSessionManager.lastSessionId === sessionId
    ) {
      return
    }
    if (sessionId) {
      ChromecastSessionManager.lastSessionId = sessionId
    }

    ChromecastSessionManager.hasConnected = true
    this.player.trigger('chromecastConnected')
    this.player.options_.chromecast?.onConnected?.()

    const audioTracks = this.player.audioTracks?.() as unknown as AudioTrackList
    const textTracks = this.player.textTracks?.() as unknown as TextTrackList
    const initialAudio = getSelectedAudioTrack(audioTracks)
    const initialText = getSelectedTextTrack(textTracks)

    ChromecastSessionManager.prevTechState = {
      startTime: this.player.currentTime(),
      muted: currentMuted,
      paused: this.player.paused(),
      volume: currentVolume,
      audioTracks,
      textTracks,
      initialAudio,
      initialText,
      isMobile: isMobile(),
    }

    // clear html5+vhs tracks to fill it with receiver player tracks

    this._reloadTech()
  }

  /**
   * Create remote player instances.
   */
  createRemotePlayer() {
    if (ChromecastSessionManager.remotePlayerController) return

    ChromecastSessionManager.remotePlayer = new cast.framework.RemotePlayer()
    ChromecastSessionManager.remotePlayerController =
      new cast.framework.RemotePlayerController(
        ChromecastSessionManager.remotePlayer
      )
  }

  /**
   * Opens the Chromecast casting menu by requesting a CastSession.
   */
  openCastMenu() {
    // Do not cast if there is no media item loaded in the player
    if (!this.player.currentSource()) return

    this.getCastContext()
      .requestSession()
      .then(this._onSessionSuccess, (_err) => {
        // console.log('User closed cast window, ', _err)
      })
  }

  /**
   * Current session receiver device name.
   */
  getDeviceName() {
    return this._getCurrentSession()?.getCastDevice().friendlyName ?? ''
  }

  /**
   * Reloads the Video.js player's Tech. This causes the player to re-evaluate which
   * Tech should be used for the current source by iterating over available Tech and
   * calling `Tech.isSupported` and `Tech.canPlaySource`.
   */
  _reloadTech() {
    const player = this.player

    // this.player.currentSources() may become corrupted after reloadTech
    player.src(this.player.cachedSource)
  }

  getCastContext() {
    return cast.framework.CastContext.getInstance()
  }

  _getCurrentSession() {
    return this.getCastContext().getCurrentSession()
  }
}

ChromecastSessionManager.isChromecastConnected = (): boolean => {
  return (
    isChromecastAPIAvailable() &&
    cast.framework.CastContext.getInstance().getCastState() ===
      cast.framework.CastState.CONNECTED &&
    ChromecastSessionManager.hasConnected
  )
}

ChromecastSessionManager.isChromecastSupported = (): boolean => {
  return isChromecastAPIAvailable()
}
